import React, { useState, useEffect } from 'react';
import { EFFECTS_REGISTRY } from '../registry';
import useTranslation from '../../hooks/useTranslation';

const EffectDrawer = ({ 
  isOpen, 
  onClose, 
  onSelectEffect, 
  categoryId, 
  categoryName,
  effects,
  direction = 'forward' // 'forward' | 'back'
}) => {
  const { t } = useTranslation();
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsAnimating(true);
      // Reset animação após transição
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(true);
      // Delay para permitir animação de saída
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  // Mapear efeitos para dados completos
  const DEFAULT_ILLUSTRATION = '/assets/effects/blur/GaussianBlur.webp';

  const effectsData = effects
    .map(id => EFFECTS_REGISTRY[id])
    .filter(Boolean)
    .map(config => ({
      id: config.id,
      name: t(`effects.names.${config.id}`),
      description: t(`effects.descriptions.${config.id}`),
      icon: config.icon,
      color: config.color,
      illustration: config.illustration || DEFAULT_ILLUSTRATION
    }));

  return (
    <div 
      className={`absolute inset-0 flex flex-col z-30 transform transition-transform duration-300 ease-in-out ${
        isOpen && !isAnimating 
          ? 'translate-x-0' 
          : direction === 'back' 
            ? '-translate-x-full' 
            : 'translate-x-full'
      }`}
      style={{ borderLeft: '1px solid rgba(255,255,255,0.08)' }}
    >
      {/* Header do Drawer */}
      <div className="p-3 border-b" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors hover:opacity-90"
            title={t('common.back')}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-white font-semibold" style={{ fontSize: '16.8px' }}>
            {categoryName}
          </h3>
        </div>
      </div>

      {/* Lista de Efeitos */}
      <div className="flex-1 overflow-y-auto">
        <div>
          {effectsData.map((effect, index) => (
            <button
              key={effect.id}
              onClick={() => onSelectEffect(effect.id)}
              className="w-full px-3 py-2.5 transition-colors group hover:opacity-90"
              style={{
                animationDelay: `${index * 50}ms`,
                animation: isOpen ? 'slideInFromRight 0.4s ease-out forwards' : 'none',
              }}
            >
              {effect.illustration ? (
                <div className="relative w-full">
                  <img 
                    src={effect.illustration} 
                    alt={effect.name} 
                    className="w-full object-cover" 
                    style={{ 
                      height: '102px',
                      minHeight: '102px',
                      borderRadius: '12px'
                    }} 
                  />
                  <div className="absolute inset-x-0 bottom-0 px-3 py-1.5 bg-gradient-to-t from-black/96 to-transparent" style={{ borderRadius: '0 0 12px 12px' }}>
                    <p className="font-semibold text-white drop-shadow" style={{ fontSize: '13px' }}>
                      {effect.name}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="w-full flex items-center justify-center" style={{ 
                  height: '102px',
                  minHeight: '102px',
                  backgroundColor: '#28380f',
                  borderRadius: '12px'
                }}>
                  <p className="font-semibold text-white" style={{ fontSize: '13px' }}>
                    {effect.name}
                  </p>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Estilos CSS para animações */}
      <style jsx>{`
        @keyframes slideInFromRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutToRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
        
        /* Animação de entrada escalonada para os itens */
        .effect-item {
          opacity: 0;
          transform: translateX(20px);
        }
        
        .effect-item.animate {
          animation: slideInFromRight 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default EffectDrawer;
